<script setup lang="ts">
import _ from 'lodash';
import { ref, type PropType, toRaw, computed, onMounted, onBeforeUnmount } from 'vue';
import { ContainerTypes, getContainer, getContext } from '@dandelion/core';
import CaseListHeadComponent from './CaseListHeaderComponent.vue';
import CaseListRowComponent from './CaseListRowComponent.vue';
import NonPaginatedCaseListComponent from './NonPaginatedCaseListComponent.vue';
import PaginatedCaseList from './PaginatedCaseListComponent.vue';
import DeletionDialog from '../Dialogs/DeletionDialogComponent.vue';
import PermaDeletionDialog from '../Dialogs/PermaDeletionDialogComponent.vue';
import { VariableCaseListColumn } from '@dandelion/core';
import type { ReadonlyCaseList } from '@dandelion/core/src/entities/CaseListElement';
import { ApplicationError, AppErrorType } from '@dandelion/core';
import type { CaseListActionParams, CaseServiceInterface } from '@dandelion/core';
import type { CaseListActionInterface } from '@dandelion/core';
import type { CaseListColumn } from '@dandelion/core';
import { CaseListRowStyleInterface } from '@dandelion/core';
import type { CaseListServiceInterface } from '@dandelion/core/src/interfaces/CaseListServiceInterface';
import type CurrentContextService from '@dandelion/core/src/services/CurrentContextService';
import type TranslationService from '@dandelion/core/src/services/TranslationService';
import { Contexts } from '@dandelion/core';
import { NamedBindings } from '@dandelion/core';
import { ApplicationEvents } from '@dandelion/core';
import { OpenCaseRequested } from '@dandelion/core';
import { OpenDeletionDialogRequested } from '@dandelion/core';
import { isCaseWithError } from '@dandelion/core';


const props = defineProps({
  caseListColumns: {
    type: Array as PropType<Array<CaseListColumn>>,
    required: false,
    default: () => new Array<CaseListColumn>(),
  },
  caseListRowStyles: {
    type: Array as PropType<Array<CaseListRowStyleInterface>>,
    required: false,
    default: () => new Array<CaseListRowStyleInterface>(),
  },
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  additionalActions: {
    type: Array as PropType<Array<CaseListActionInterface>>,
    required: false,
    default: () => [],
  },
  addDefaultActions: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: true,
  },
  commonPagination: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
  numberOfElementsPerPage: {
    type: Number as PropType<number>,
    required: false,
    default: 20,
  },
  openOnLineClick: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
});

const container = getContainer(props.context);
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;
const currentContext = container.get(NamedBindings.CurrentContextService) as CurrentContextService;
const ts = container.get(NamedBindings.TranslationService) as TranslationService;
const isUpdatingCaseList = caseListService.isUpdatingCaseList();
const caseList: ReadonlyCaseList = caseListService.slicedCases;
const deletionDialogVisibility = ref(false);
const permaDeletionDialogVisibility = ref(false);
const deletingCaseID = ref('');
const rowElement = ref();
const arrayOfVariablesToBeCached = new Array<string>();

// if there are columns that are not cached, throw an error
const cachedVariables = currentContext.getCaseConfiguration().cachedVariables;
const caseListVariables = toRaw(props.caseListColumns) .filter((column) => column instanceof VariableCaseListColumn)
  .map((column) => (column as VariableCaseListColumn).getVariableName());


if (_.intersection(caseListVariables, cachedVariables).length < caseListVariables.length) {
  throw new ApplicationError('All columns must be cached in case configuration', AppErrorType.COLUMNS_NOT_CACHED);
}

props.caseListColumns.forEach((caseListColumn: CaseListColumn) => {
  if (caseListColumn instanceof VariableCaseListColumn) {
    arrayOfVariablesToBeCached.push(caseListColumn.getVariableName());
  }
});

caseListService.startList.value = 0;
caseListService.endList.value = props.numberOfElementsPerPage;

const defaultActions = [
    {
    name: 'openCase',
    actionClass: 'open-case',
    label: 'Open case',
    action: (values: CaseListActionParams) =>
      getContext(values.rowProps.context).dispatchEvent(
        ApplicationEvents.OpenCaseRequested,
        new OpenCaseRequested(ContainerTypes.DEFAULT, values.element.caseMetaData.caseID)
      ),
    isVisible: (values: CaseListActionParams) => !values.rowProps.openOnLineClick,
  },
  {
    name: 'deleteCase',
    actionClass: 'delete-case',
    label: 'Delete case',
    icon: 'trash',
    tooltip: ts.t('Delete case'),
        action: (values: CaseListActionParams) =>{
          const event =  new OpenDeletionDialogRequested(ContainerTypes.DEFAULT, values.element.caseMetaData.caseID) as OpenDeletionDialogRequested & { rowElement?: string };
          event.rowElement = values.element;
      getContext(values.rowProps.context).dispatchEvent(
        ApplicationEvents.OpenDeletionDialogRequested,
        event
      )},

    },
  
];
const arrayOfActions: Array<CaseListActionInterface> = [
  ...props.additionalActions,
  ...(props.addDefaultActions ? defaultActions : []),
];


function closeDeletionDialog() {
  deletionDialogVisibility.value = false;
}

function showPermaDeletionDialog() {
  permaDeletionDialogVisibility.value = true;
}

function closePermaDeletionDialog() {
  permaDeletionDialogVisibility.value = false;
}

async function updateCurrentPage({ startIndex, endIndex }: { startIndex: number; endIndex: number }) {
  caseListService.startList.value = startIndex;
  caseListService.endList.value = endIndex;
  await caseListService.updateCaseList();
}
caseListService.updateCaseList();

const areFiltersActive = computed(() => caseListService.areFiltersActive.value);
const emit = defineEmits(['cleanfilters'])

function emitCleanFilters() {
  emit('cleanfilters', 'Clean Filters')
}
let tokenForListener: string | null = null;
onMounted(() => {
  tokenForListener = getContext(props.context).addEventListener(
    ApplicationEvents.OpenDeletionDialogRequested,
    (_, event) => {
            const e = event as OpenDeletionDialogRequested & {
        rowElement?: string;
      };
      rowElement.value = e.rowElement;
      deletingCaseID.value = e.caseID;
      deletionDialogVisibility.value = true;
    }
  );
});

onBeforeUnmount(() => {
  if (tokenForListener) {
    getContext(props.context).removeEventListenerByToken(tokenForListener);
  }
});

</script>

<template>
  <div class="case-list-wrapper">
    <DeletionDialog :context="context" :deleting-case-i-d="deletingCaseID" :row-element="rowElement"
      :deletion-dialog-visibility="deletionDialogVisibility" @close-deletion="closeDeletionDialog"
      @show-perma-deletion="showPermaDeletionDialog" />
    <PermaDeletionDialog :context="context" :deleting-case-i-d="deletingCaseID" :row-element="rowElement"
      :perma-deletion-dialog-visibility="permaDeletionDialogVisibility"
      @close-perma-deletion="closePermaDeletionDialog"
    />

    <section class="case-list">
    
      <section class="activeFilterLabel" v-if="areFiltersActive">
        <span v-html="ts.t('Active Filter!')" />
               <button
          id="reset-btn"
          type="reset"
          class="secondary"
          @click="emitCleanFilters"
          v-html="ts.t('Clean')"
        ></button>
      </section>
      <section v-if="isUpdatingCaseList" class="loading-case-list-msg loading-msg case-list-msg">
        <span v-html="ts.t('Loading case list...')" />
      </section>

      <section v-else-if="caseListService.allCases.length === 0"
        class="no-cases-in-list-msg no-cases-msg case-list-msg">
        <span v-html="ts.t('No Cases In List')" />
      </section>
      <section
        v-else-if="areFiltersActive && caseListService.filteredCases.length === 0"
        class="no-items-found-in-list-msg case-list-msg"
      >
        <span v-html="ts.t('No found items')" />
      </section>
      <table v-else class="case-list-table">
        <CaseListHeadComponent :case-list-columns="caseListColumns" />
        <tbody>
          <CaseListRowComponent
            v-for="(row, rowIndex) in caseList"
            :row-index="rowIndex"
            :key="isCaseWithError(row) ? row.caseID : row?.caseMetaData?.caseID"
            :case-list-columns="caseListColumns"
            :case-list-row-styles="caseListRowStyles"
            :context="context"
            :actions="arrayOfActions"
            :open-on-line-click="openOnLineClick"
          />
        </tbody>
      </table>

  
    </section>
        <template v-if="!commonPagination">
        <NonPaginatedCaseListComponent
          :context="context"
          :number-of-elements-per-page="numberOfElementsPerPage"
          :deleted-case-list="false"
          @update="updateCurrentPage"
        />
      </template>
      <template v-if="commonPagination">
        <PaginatedCaseList
          :context="context"
          :number-of-elements-per-page="numberOfElementsPerPage"
          :deleted-case-list="false"
          @update="updateCurrentPage"
        />
      </template>
  </div>
</template>
